import type { ViewLineMarks } from '../print/lineMarks';
import type { SendMailbox } from '../records/companyMailbox';
import type { DocumentDeliveryView } from './delivery';
import { cents, type Currency } from '../money';
import type { ApprovalView } from './approvalGate';
import { dependantReason, type LineDependant } from './lineIdentity';
import type { PrintDocument } from './printRegions';
import type { PrintSheetLayout, PrintSheetHtml } from '../print/templates';
import type { ApprovalStatusValue, BranchStatusValue, FulfillmentStatusValue, SalesOrderStatusValue } from './statuses';
import { approvalCleared, documentTransitionAllowed, fulfillmentFromBranches, fulfillmentMoveAllowed } from './statuses';
import type { DividerRow } from '../composites/itemTable';
import type { FrozenDealTeam } from './frozenDealTeam';
import type { SalesOrderNext } from './whatsNext';

/**
 * Piece 22, decision 0018: the Work Order to-do the Sales Order raises, closed by
 * the Work Order existing.
 *
 * 🔨 It lives here rather than in the shared package's own index, because a
 * constant declared in a barrel makes that barrel a file the walk scope cannot see
 * through: every page reads something through the index, so a line added there
 * would walk all of them (decision 0625).
 */
export const SALES_ORDER_WORK_ORDER_OFFER_KIND = 'SalesOrderWorkOrderOffer';

/**
 * 🔒 The `act` an order's page opens a raise window on (decision 0863): the Work
 * Order to-do links to the order with the first, so it opens the same raise the
 * title bar offers.
 */
export const RAISE_WORK_ORDER_ACT = 'raiseWorkOrder';
export const RAISE_PURCHASE_ORDER_ACT = 'raisePurchaseOrder';

/**
 * The Sales Order's own rules as pure functions (piece 22): the customer's
 * purchase-order requirement and the waiver that lets a verbal authorisation
 * through, the release gate that reads both, the parts-and-labour fork, the
 * contract value a change order adds to, and the over-billing exception. Each
 * is here rather than in the service so the write path, the screen and the
 * tests read one copy.
 */

/** 🔨 A Project always needs the customer's number; every other type needs one above the Organization's threshold, unioned with the Company's own flag (decision 0166). */
export function customerPoRequired(input: { dealType: 'Project' | 'Parts' | 'Service' | null; requiresPoNumber: boolean; totalCents: number; thresholdCents: number }): boolean {
  if (input.dealType === 'Project') return true;
  if (input.requiresPoNumber) return true;
  return input.totalCents > input.thresholdCents;
}

/** Why the requirement applies, for the screen and for the refusal; null when no rule demands a number. */
export function customerPoReason(input: { dealType: 'Project' | 'Parts' | 'Service' | null; requiresPoNumber: boolean; totalCents: number; thresholdCents: number }): string | null {
  if (input.dealType === 'Project') return 'A Project needs the customer’s purchase order number at any value';
  if (input.requiresPoNumber) return 'This customer requires a purchase order number on every order';
  if (input.totalCents > input.thresholdCents) return `This order is above the value at which a customer purchase order number is required`;
  return null;
}

/** 🔒 The reason line the page draws: null once a number is recorded or the requirement is waived, because the sentence then answers a question nobody has (T982; decision 10690). */
export function customerPoReasonLine(input: Parameters<typeof customerPoReason>[0], state: { customerPoNumber: string | null; waived: boolean }): string | null {
  if (state.waived || (state.customerPoNumber ?? '').trim() !== '') return null;
  return customerPoReason(input);
}

/** 🔨 A waiver is a reason and an authoriser together; one without the other is not a waiver. */
export function waiverProblem(reason: string | null | undefined, authorisedByPersonId: string | null | undefined): string | null {
  if (!reason?.trim()) return 'waiving the customer purchase order requirement names a reason';
  if (!authorisedByPersonId) return 'waiving the customer purchase order requirement names who authorized it';
  return null;
}

/**
 * 🔒 The file type a customer's own purchase order is uploaded under
 * (decision 0457). It is one seeded name, because the release reads it: a
 * type a person typed would make the requirement depend on their spelling.
 */
export const CUSTOMER_PO_FILE_TYPE_NAME = 'Customer Purchase Order';

export interface SalesOrderReleaseFacts {
  status: SalesOrderStatusValue;
  poRequired: boolean;
  customerPoNumber: string | null;
  waived: boolean;
}

/**
 * 🔨 The move to Released is refused while the customer's number is required
 * and empty and unwaived. The transition table is asked first, so a Draft
 * order is refused for the move rather than for the number.
 *
 * 🔒 The number releases the order and the customer's own purchase order is
 * optional (decision 2091, amending 0457; T1189). The owner's answer of
 * 2026-10-05 made an accepted quote a won deal, and the walk found the order
 * held on the document after the number was in. The waiver stays for an order
 * with no number at all.
 */
export function salesOrderReleaseProblem(facts: SalesOrderReleaseFacts): string | null {
  if (!documentTransitionAllowed('SalesOrderStatus', facts.status, 'Released')) return `a Sales Order at ${facts.status} does not move to Released`;
  if (facts.waived) return null;
  if (facts.poRequired && !facts.customerPoNumber?.trim()) return 'The customer’s purchase order number is required before this order releases; record it, or waive the requirement with a reason and an authorizer';
  return null;
}

/** 🔨 A Work Order and a Shipment wait on the release, not on the order existing: an order at Pending Customer PO moves nothing. */
export function releasedForWorkProblem(status: SalesOrderStatusValue): string | null {
  if (status === 'Released' || status === 'Closed') return null;
  return `the Sales Order is ${status}; work and shipments move against a Released order`;
}

/** The two branch columns after a write, with the fulfilment recomputed from them; Cancelled is a person's write and is never recomputed. */
export function branchWrites(
  current: { partsBranchStatus: BranchStatusValue; labourBranchStatus: BranchStatusValue; fulfillmentStatus: FulfillmentStatusValue },
  patch: { partsBranchStatus?: BranchStatusValue; labourBranchStatus?: BranchStatusValue },
): { partsBranchStatus: BranchStatusValue; labourBranchStatus: BranchStatusValue; fulfillmentStatus: FulfillmentStatusValue } {
  const parts = patch.partsBranchStatus ?? current.partsBranchStatus;
  const labour = patch.labourBranchStatus ?? current.labourBranchStatus;
  const fulfillmentStatus = current.fulfillmentStatus === 'Cancelled' ? 'Cancelled' : fulfillmentFromBranches(parts, labour);
  return { partsBranchStatus: parts, labourBranchStatus: labour, fulfillmentStatus };
}

/** 🔒 The order of a branch's progress, so a document's move never takes a branch backwards (T913, decision 1711). */
const BRANCH_RANK: Record<Exclude<BranchStatusValue, 'NotApplicable'>, number> = { NotStarted: 0, InProgress: 1, Complete: 2 };

const forwardOnly = (current: BranchStatusValue, reached: Exclude<BranchStatusValue, 'NotApplicable'>): BranchStatusValue => {
  if (current === 'NotApplicable') return current;
  return BRANCH_RANK[reached] > BRANCH_RANK[current] ? reached : current;
};

/** What the documents behind a Sales Order say about its two branches (T913, decision 1711). */
export interface SalesOrderBranchFacts {
  /** Each goods line's quantity and how much of it has left: shipped, or used on a job (`quantityConsumed`). */
  goodsLines: readonly { quantity: number; consumed: number }[];
  /** Purchase orders naming the order and not canceled. */
  purchaseOrders: number;
  /** Shipments against the order that have shipped. */
  shipments: number;
  /** The status of each Work Order carrying one of the order's lines, a voided one left out. */
  workOrders: readonly string[];
}

/**
 * 🔒 The two branches as the order's documents move them (T913, decision 1711).
 * Parts: In progress on its first purchase order or shipment, Complete when
 * every goods line has left in full. Labour: In progress with its Work Order,
 * Complete when every one is Complete or Closed. A branch marked N/A stays
 * N/A, and a document never takes a branch backwards.
 */
export function systemBranches(
  current: { partsBranchStatus: BranchStatusValue; labourBranchStatus: BranchStatusValue },
  facts: SalesOrderBranchFacts,
): { partsBranchStatus: BranchStatusValue; labourBranchStatus: BranchStatusValue } {
  const shippedInFull = facts.goodsLines.length > 0 && facts.goodsLines.every((line) => line.consumed >= line.quantity);
  const partsStarted = facts.purchaseOrders > 0 || facts.shipments > 0 || facts.goodsLines.some((line) => line.consumed > 0);
  const parts = shippedInFull ? 'Complete' : partsStarted ? 'InProgress' : 'NotStarted';
  const jobs = facts.workOrders;
  const labour = jobs.length === 0 ? 'NotStarted' : jobs.every((status) => status === 'Complete' || status === 'Closed') ? 'Complete' : 'InProgress';
  return { partsBranchStatus: forwardOnly(current.partsBranchStatus, parts), labourBranchStatus: forwardOnly(current.labourBranchStatus, labour) };
}

/**
 * 🔒 The branches a converted order opens at (T913, decision 1711): a branch
 * with no line of its kind opens Not applicable, so the join never waits on
 * work nobody sold. A line naming no Item is neither kind.
 */
export function openingBranches(lines: readonly { itemType: 'Good' | 'Service' | null }[]): { partsBranchStatus: BranchStatusValue; labourBranchStatus: BranchStatusValue } {
  return {
    partsBranchStatus: lines.some((line) => line.itemType === 'Good') ? 'NotStarted' : 'NotApplicable',
    labourBranchStatus: lines.some((line) => line.itemType === 'Service') ? 'NotStarted' : 'NotApplicable',
  };
}

/** 🔨 The recomputation passes the same guard every other write does, so a branch completing on an unapproved order moves nothing (decision 0093). */
export function branchMoveProblem(current: { fulfillmentStatus: FulfillmentStatusValue; approvalStatus: ApprovalStatusValue }, next: FulfillmentStatusValue): string | null {
  if (next === current.fulfillmentStatus) return null;
  if (!fulfillmentMoveAllowed(current.fulfillmentStatus, next, current.approvalStatus)) {
    return current.approvalStatus === 'Approved'
      ? `fulfillment does not move from ${current.fulfillmentStatus} to ${next}`
      : `fulfillment cannot leave ${current.fulfillmentStatus} while the order’s approval is ${current.approvalStatus}`;
  }
  return null;
}

export interface ChangeOrderValue {
  approvalStatus: ApprovalStatusValue;
  /** The change order's own lifecycle status; a Withdrawn one counts toward nothing whatever its approval axis says. */
  status?: string;
  linesCents: number;
}

/** 🔨 Cleared is Approved or NotRequired: a change order inside the submitter's rails needs nobody's signature (decision 0410). */
export function changeOrderCleared(order: { approvalStatus: ApprovalStatusValue; status?: string }): boolean {
  if (order.status === 'Withdrawn') return false;
  return approvalCleared(order.approvalStatus);
}

/**
 * 🔨 A change order the gate has cleared is part of the contract value, so it
 * is reversed by a further change order and never withdrawn (decision 0410).
 * Withdrawal belongs to one still waiting, or one the gate turned down.
 */
export function withdrawChangeOrderProblem(order: { status: string; approvalStatus: ApprovalStatusValue }): string | null {
  if (order.status === 'Withdrawn') return 'this change order is already withdrawn';
  if (changeOrderCleared(order)) return 'a cleared change order is part of the contract value; a further change order reverses it';
  return null;
}

/**
 * 🔒 A change order is edited until something depends on it (decision 0486;
 * product owner, 2026-09-17: *"Allow the change order to be editable until
 * something depends on"*).
 *
 * 💰 Clearing the gate used to freeze it, which meant a change order inside
 * the submitter's own rails was created `NotRequired` and frozen by the same
 * save — approved and unchangeable before anybody had read it. That was
 * decision 0475's rule and it made the ordinary case the unreachable one.
 *
 * 🔨 What freezes it is a dependant, not an approval: a line shipped, fitted
 * at a job, billed, credited, allocated or received is work somebody has
 * already done against the agreed scope, and a further change order is what
 * changes it. A withdrawn change order is fixed for its own reason — it counts
 * toward nothing, and reopening it would resurrect it.
 */
export interface ChangeOrderEditFacts {
  status: string;
  approvalStatus: ApprovalStatusValue;
  /** What already points at this change order's lines, gathered across every line; empty where nothing does. */
  dependants: readonly LineDependant[];
}

export function editChangeOrderProblem(order: ChangeOrderEditFacts): string | null {
  if (order.status === 'Withdrawn') return 'a withdrawn change order is not edited; raise another one';
  if (order.dependants.length > 0) return `this change order is no longer edited because ${dependantReason(order.dependants)}; raise a further change order to change what was agreed`;
  return null;
}

/** Whether the screen offers the change order's own Edit; the server answers it and the page never works it out (decision 0474). */
export function changeOrderEditable(order: ChangeOrderEditFacts): boolean {
  return editChangeOrderProblem(order) === null;
}

/**
 * 🔨 What editing a cleared change order costs, said before the person starts
 * rather than discovered after they save (decision 0486). The save re-routes
 * it through the gate, so the contract value stops counting it until it clears
 * again. Null where the change order has not cleared and there is nothing to
 * warn about.
 */
export function reopenClearedChangeOrderWarning(order: ChangeOrderEditFacts): string | null {
  // 🔨 Nothing to warn about where the edit is refused anyway: a warning beside a control that is not there reads as a second refusal (found on the database run, 2026-09-17).
  if (!changeOrderEditable(order)) return null;
  if (!changeOrderCleared(order)) return null;
  return 'this change order has cleared and its lines count toward the contract value; saving a change sends it back through approval, and the contract value drops until it clears again';
}

/**
 * 🔨 Contract value is derived, never stored: the order's own lines plus the
 * lines of every change order the approval gate has cleared (decision 0410).
 * A pending change order counts toward nothing.
 */
export function contractValueCents(orderLinesCents: number, changeOrders: readonly ChangeOrderValue[]): number {
  return orderLinesCents + changeOrders.filter(changeOrderCleared).reduce((sum, c) => sum + c.linesCents, 0);
}

/** The contract value as money, with the pending amount named beside it so a project manager sees what is waiting. */
export function contractValue(orderLinesCents: number, changeOrders: readonly ChangeOrderValue[], currencyCode = 'USD'): { agreed: Currency; pending: Currency } {
  return {
    agreed: cents(contractValueCents(orderLinesCents, changeOrders), currencyCode),
    pending: cents(changeOrders.filter((c) => c.status !== 'Withdrawn' && c.approvalStatus === 'Pending').reduce((sum, c) => sum + c.linesCents, 0), currencyCode),
  };
}

/**
 * 🔒 The account shipping charges post to (piece 23's chart), which the
 * over-billing rule leaves out on both sides (decision 1703): shipping is
 * prepay-and-add a lot of the time, so a freight line billed beyond the order
 * is not billing past it.
 */
export const SHIPPING_REVENUE_ACCOUNT_NUMBER = '4200';

/** The over-billing refusal's own words, which the invoice form reads to open the reason and the authoriser (T918). */
export const INVOICE_OVERRUN_REFUSAL = 'the invoices against this Sales Order would sum past its total, shipping charges aside; that needs a reason and an authorizer';

/**
 * 💰 The sum of invoices against a Sales Order cannot exceed its total without
 * a reason and an authoriser — the same escape-hatch shape as the credit-note
 * bypass, and it is reportable. 🔒 Every figure is read without the lines on
 * 4200 Shipping Revenue, so an overrun made of shipping charges alone is not
 * an overrun (decision 1703).
 */
export function invoiceOverrunProblem(input: { orderTotalCents: number; alreadyInvoicedCents: number; addingCents: number; reason?: string | null; authorisedByPersonId?: string | null }): string | null {
  const after = input.alreadyInvoicedCents + input.addingCents;
  if (after <= input.orderTotalCents) return null;
  if (input.reason?.trim() && input.authorisedByPersonId) return null;
  return INVOICE_OVERRUN_REFUSAL;
}

function convertedFieldRefusal(field: string): string {
  return `the ${field} came from the accepted Quote and is not changed on this Sales Order; raise a revised Quote, or a change order once the order is released`;
}

/**
 * 🔒 The header fields a Sales Order converted from a Quote holds read-only
 * (piece 22): the two sites and the display mode are what the customer agreed
 * to on the Quote, and a directly created order sets them itself. A save that
 * sends a field back unchanged, or does not send it at all, changes nothing and
 * is not refused; only a different value is.
 */
export function convertedOrderRefusal(
  stored: { billToSiteId: string | null; shipToSiteId: string | null; displayMode: string },
  sent: { billToSiteId?: string | null | undefined; shipToSiteId?: string | null | undefined; displayMode?: string | undefined },
): string | null {
  if (sent.billToSiteId !== undefined && sent.billToSiteId !== stored.billToSiteId) return convertedFieldRefusal('Bill To site');
  if (sent.shipToSiteId !== undefined && sent.shipToSiteId !== stored.shipToSiteId) return convertedFieldRefusal('Ship To site');
  if (sent.displayMode !== undefined && sent.displayMode !== stored.displayMode) return convertedFieldRefusal('display mode');
  return null;
}

/** A change order's lines can be negative, so a removal is recorded as a change order rather than as an edit to the signed original. */
export function changeOrderLineSignAllowed(): boolean {
  return true;
}

export type SalesOrderPrimaryAction = 'send' | 'recordCustomerPo' | 'release' | 'invoice' | null;

export interface SalesOrderFooter {
  primary: SalesOrderPrimaryAction;
  /** Offered in every status but Void and Closed; the server refuses on its own terms whatever the footer offers. */
  mayVoid: boolean;
  /**
   * 🔒 The waiver stands while the release waits on the customer's purchase
   * order number (decisions 1693, 2091): the document no longer holds the
   * release, so the waiver goes once the number is in.
   */
  mayWaive: boolean;
  mayRaiseWorkOrder: boolean;
  mayRaiseShipment: boolean;
  mayRaiseDropShip: boolean;
  /**
   * 🔒 Send again, beside the primary, on an order past Draft and short of Void (decision 2630): it mails the copy the
   * customer holds and moves nothing. A Draft's Send is the primary itself. Absent from an older reply.
   */
  mayResend?: boolean;
  /** The reason the primary is disabled, where the gate or the missing number holds it. */
  refusal: string | null;
}

/** The footer's one primary action per status, with the server's own refusal on the control (piece 22, the Quote's shape). */
export function salesOrderFooter(facts: { status: SalesOrderStatusValue; approvalStatus: ApprovalStatusValue; poRequired: boolean; customerPoNumber: string | null; waived: boolean; invoiceLeft?: boolean }): SalesOrderFooter {
  const held = !approvalCleared(facts.approvalStatus);
  const released = facts.status === 'Released';
  const closedOrVoid = facts.status === 'Closed' || facts.status === 'Void';
  // 🔒 The footer asks for the number alone; the customer's own purchase order is optional (decision 2091, amending 0457 and 1693).
  const needsNumber = facts.poRequired && !facts.customerPoNumber?.trim() && !facts.waived;
  const primary: SalesOrderPrimaryAction = closedOrVoid
    ? null
    : facts.status === 'Draft'
      ? 'send'
      : released
        // 🔒 Raise an Invoice stands while anything is left to invoice (decision 1931); absent reads as something.
        ? facts.invoiceLeft === false ? null : 'invoice'
        : needsNumber
          ? 'recordCustomerPo'
          : 'release';
  return {
    primary,
    mayVoid: !closedOrVoid,
    mayWaive: !closedOrVoid && !released && needsNumber,
    mayRaiseWorkOrder: released,
    mayRaiseShipment: released,
    mayRaiseDropShip: released,
    mayResend: facts.status !== 'Draft' && facts.status !== 'Void',
    refusal: held
      ? `The approval gate holds this order: it is ${facts.approvalStatus}`
      : needsNumber
        ? 'The customer’s purchase order number is not recorded yet'
        : null,
  };
}

/**
 * 🔒 The Sales Order as its own routes answer it, declared once so the service
 * that builds it and the screen that reads it are the same shape (piece 22;
 * decision 0409).
 */
export interface SalesOrderLineRow extends ViewLineMarks {
  id: string;
  lineNumber: number;
  itemId: string | null;
  /** The Item's own name, which the edit form reopens on and the sheet's Item cell leads with (T118; decision 0640). */
  itemName: string | null;
  sku: string | null;
  description: string;
  quantity: number;
  quantityConsumed: number;
  unitId: string;
  unitName: string;
  unitPriceCents: number | null;
  amountCents: number;
  /** 🔨 The line's own tax control and the calculation's own answer for it (decisions 0086, 0435); the amount is never typed. */
  taxCategoryId: string;
  taxCategoryName: string;
  taxAmountCents: number;
  /** 🔨 The account the line posts to, resolved once at creation with the line's own override first (decisions 0097, 0435). */
  accountId: string;
  accountLabel: string;
  /** Piece 22: per-line invoiced state, derived from the Invoice lines pointing here and never stored. */
  invoicedQuantity: number;
  invoicedLabel: string;
  /** 🔒 The hours the sale budgets on a service line (decision 0860); null on every other line. Absent from an older reply. */
  budgetedHours?: number | null;
  /** 🔒 Whether the line's Item is a Service, which is what draws the budget cell (decision 0860). Absent from an older reply. */
  isService?: boolean;
  /** 🔒 The line's own discount, `Percentage` or `Flat`, which the edit form reopens on (decision 1031); absent from an older reply. */
  discountType?: string | null;
  discountValue?: number | null;
  /** 🔒 The part a drop-ship order not yet confirmed ships from the vendor (decision 1930); absent from an older reply. */
  dropShipAwaiting?: number;
  /** 🔒 A time and materials service line, billed by its job's invoice and never the order's (decision 2591); absent from an older reply. */
  billsFromJob?: boolean;
  /** 🔒 An allowance the quote carried here, billed at actuals from the job (decision 2612); absent from an older reply. */
  isAllowance?: boolean;
}

export interface ChangeOrderRow {
  id: string;
  changeOrderNumber: string;
  sequence: number;
  status: string;
  approvalStatus: string;
  reason: string;
  description: string;
  totalCents: number;
  createdAt: string;
  /** 🔒 The day the change order is dated, `YYYY-MM-DD` (decision 0681). */
  documentDate: string;
}

/**
 * 🔒 One line of a Change Order as its own page reads it (decision 0475). It
 * is the Sales Order line row without the invoiced state and without the
 * posting account: a change order's lines are never billed directly, and its
 * column set draws no account column.
 */
export interface ChangeOrderLineRow {
  id: string;
  lineNumber: number;
  itemId: string | null;
  /** The Item's own name, which the edit form reopens the line on, as the Sales Order's does (T319; decision 0640). */
  itemName: string | null;
  sku: string | null;
  description: string;
  /** 🔨 Negative where the change order takes scope away; the screen neither blocks it nor draws it as an error (decision 0123). */
  quantity: number;
  unitId: string;
  unitName: string;
  unitPriceCents: number | null;
  amountCents: number;
  taxCategoryId: string;
  taxCategoryName: string;
  taxAmountCents: number;
  accountId: string;
  /** 🔒 The line's own discount, which the edit form reopens on (decision 1031); absent from an older reply. */
  discountType?: string | null;
  discountValue?: number | null;
}

/**
 * 🔒 The Change Order as its own route answers it (decision 0475): the row the
 * parent order lists, plus what the page needs to draw it and the edit mode
 * needs to reopen it. `editable` is the server's answer, not the screen's
 * (decision 0474).
 */
export interface ChangeOrderAnswer extends ChangeOrderRow {
  /** 🔒 Whether this viewer may approve the document now, the Pending Approvals queue's own answer for this one document (T644; decision 1521); absent reads as no. */
  canApprove?: boolean;
  /** 🔒 Why the document rests where it does, and a Draft's one primary action (T131, T223; decision 0713); absent from an older reply. */
  approval?: ApprovalView;
  /** 🔒 The group dividers among the lines, each at its place in the one reading order (decision 0650); absent from an older reply. */
  dividers?: DividerRow[];
  salesOrderId: string;
  /** 🔒 The layout the sheet prints on and the sheet itself, as the printer's own HTML (decision 0567; T169). */
  printLayout?: PrintSheetLayout | null;
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687). */
  writeRefusal?: string | null;
  /** 🔒 The Deal's team as it stood when this document was created (decision 0765); null or absent where it carries no Deal. */
  dealTeam?: FrozenDealTeam | null;
  salesOrderNumber: string;
  companyName: string;
  currencyCode: string;
  /** 🔒 The notes to the customer Region 7 prints (decision 0683); null where none are typed. */
  customerNotes: string | null;
  lines: ChangeOrderLineRow[];
  affectedAssetNodeIds: string[];
  escalationReason: string | null;
  rejectionReason: string | null;
  editable: boolean;
  /** Why the edit is refused, where it is; null where the change order is editable. */
  frozenReason: string | null;
  /** 🔒 Whether Withdraw is offered, and why it is not (decision 0504); the server's own answer, so the page asks rather than deciding. */
  mayWithdraw: boolean;
  withdrawRefusal: string | null;
  /** What editing this change order costs, where it has already cleared; null otherwise (decision 0486). */
  reopenWarning: string | null;
}

export interface SalesOrderAnswer {
  /** 🔒 The Account's mailbox the send window opens To on, chosen by the server for the Organization's day and never sent to an outside login (decision 2974); null where the Account holds none; absent from an older reply. */
  sendMailbox?: SendMailbox | null;
  /** 🔒 Whether this viewer may approve the document now, the Pending Approvals queue's own answer for this one document (T644; decision 1521); absent reads as no. */
  canApprove?: boolean;
  /** 🔒 Why the document rests where it does, and a Draft's one primary action (T131, T223; decision 0713); absent from an older reply. */
  approval?: ApprovalView;
  /** 🔒 The group dividers among the lines, each at its place in the one reading order (decision 0650); absent from an older reply. */
  dividers?: DividerRow[];
  id: string;
  salesOrderNumber: string;
  revision: number;
  companyId: string;
  companyName: string;
  status: SalesOrderStatusValue;
  /** 🔒 The status as a person reads it: Sending or Delivery Failed over a Sent whose send the host has not confirmed (decision 1225); absent from an older reply. */
  displayedStatus?: string;
  /** 🔒 The newest send's Delivered panel (decision 1226); null where nothing was mailed; absent from an older reply. */
  delivery?: DocumentDeliveryView | null;
  /** 🔒 The date and the threshold the send window warns on, on a Draft awaiting its first send (decision 1227); absent from an older reply. */
  staleSend?: { documentDate: string; thresholdDays: number } | null;
  approvalStatus: string;
  escalationReason: string | null;
  rejectionReason: string | null;
  fulfillmentStatus: FulfillmentStatusValue;
  partsBranchStatus: BranchStatusValue;
  labourBranchStatus: BranchStatusValue;
  customerPoNumber: string | null;
  /** 🔒 The amount the customer's purchase order covers, in cents (decision 2594); null where none is typed, absent from an older reply. */
  customerPoAmountCents?: number | null;
  /** 🔒 The warning past that amount, billed and billable to date (decision 2594); null inside it, absent from an older reply. */
  customerPoWarning?: string | null;
  /** 🔒 The account over its credit limit or on Credit Hold, in the salesperson's words (decision 3280; T2269); null inside it, absent from an older reply. It warns and blocks nothing. */
  creditWarning?: string | null;
  /** 🔒 The Deal's not-to-exceed in cents, the ceiling an invoice from its jobs is held past (decision 2612); null where none stands, absent from an older reply. */
  notToExceedCents?: number | null;
  customerPoRequired: boolean;
  customerPoReason: string | null;
  customerPoWaived: boolean;
  /** 🔒 Whether the customer's own purchase order is attached to this order (decision 0457). */
  customerPoDocumentAttached: boolean;
  sourceQuoteId: string | null;
  convertedFrom: string | null;
  dealId: string | null;
  dealName: string | null;
  billToSiteId: string;
  shipToSiteId: string | null;
  displayMode: string;
  /** 🔒 The day the order is dated, `YYYY-MM-DD` (decision 0681). */
  documentDate: string;
  /** 🔒 How the goods travel, a `ShipmentPreference` row (decision 0682); null where the order names none. */
  shipmentPreferenceId: string | null;
  /** 🔒 The notes to the customer Region 7 prints (decision 0683); null where none are typed. */
  customerNotes: string | null;
  createdAt: string;
  currencyCode: string;
  totalCents: number;
  contractValueCents: number;
  pendingChangeCents: number;
  invoicedCents: number;
  lines: SalesOrderLineRow[];
  changeOrders: ChangeOrderRow[];
  footer: SalesOrderFooter;
  /** 🔒 The one projection the page's view mode and the PDF both render, built on the server and never in the browser (piece 27). */
  printDocument: PrintDocument;
  /**
   * 🔒 The layout the document prints on, resolved once beside the projection
   * (decision 0567): view mode positions these region rows, so a region nudged
   * in the editor moves on the page. Absent where no template is laid out, and
   * the page then falls back to the reading order it drew before.
   */
  printLayout?: PrintSheetLayout | null;
  /** 🔒 The sheet both views read, as the printer's own HTML, in this same answer (T169). */
  printSheet?: PrintSheetHtml | null;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687). */
  writeRefusal?: string | null;
  /** 🔒 The Deal's team as it stood when this document was created (decision 0765); null or absent where it carries no Deal. */
  dealTeam?: FrozenDealTeam | null;
  /** 🔒 The order's two raises and where each stands (decision 0863, T282); absent from an older reply. */
  raise?: SalesOrderRaiseView;
  /** 🔨 Edit reopens a Draft and nothing else; a sent order stands with the customer (decision 0409). */
  editable: boolean;
  /** 🔒 The files attached to this order, drawn on its own page (decision 0457). */
  files: DocumentFileRow[];
  /** 🔒 The Discount Scope setting, which the change order's form reads off its order (decisions 0097, 1031); absent from an older reply. */
  discountLevel?: 'Footer' | 'Line';
  /** 🔒 What the order has left to do, which its footer and its What's next banner both read (decision 1931); absent from an older reply. */
  next?: SalesOrderNext | null;
}

/**
 * 🔒 A file attached to a document, as its own page draws it (decision 0457).
 * It is the shape the Files tile already draws on a record page, declared here
 * so a document page and that tile cannot read one file two ways.
 */
export interface DocumentFileRow {
  id: string;
  filename: string;
  fileTypeName: string;
  description: string | null;
  versions: number;
  uploadedAt: string;
  /** 🔨 `Verified` where somebody has checked the file is what it says it is; a plain upload carries none. */
  verificationStatus: string | null;
  /** 🔒 Decision 1182: the sentence a quarantined file's row carries, why and who to ask; absent or null on any other file. */
  quarantine?: string | null;
  /** 🔒 The name the served version is stored under (decision 2421), which the Files tab's filter matches beside the file's name (T1741; decision 2713). */
  storedName?: string | null;
  /**
   * 🔒 Decision 3010: what the one File row draws, read where the Files tile reads
   * it. The file's stored media type, which picks the row's icon.
   */
  mimeType?: string | null;
  /** 🔒 What the file is narrowed to, as the tile's restriction pill reads: Confidential, a class, Unclassified; null for a Public file. */
  restriction?: DocumentFileFlag | null;
  /** 🔒 Decision 1953: the newest version's verdict, when, and which scanner gave it. */
  scanVerdict?: 'Pending' | 'Clean' | 'Blocked' | null;
  scannedAt?: string | null;
  scannedBy?: string | null;
  /** 🔒 Decision 7420: the served version's bytes are stored encrypted, drawn as a lock beside the name; absent from an answer older than it. */
  encrypted?: boolean;
  /** 🔒 Decision 3012: who verified the file and when, which the row's Verified line draws with the verifier's avatar; null where nobody is recorded. */
  verifiedBy?: { name: string; avatarColor: string } | null;
  verifiedAt?: string | null;
  /** 🔒 Decisions 8960, 11480: Pending or Rejected where the file's newest open release stands so, which the row draws as its pill; absent otherwise. */
  releaseApproval?: 'Pending' | 'Rejected';
  releaseRejection?: string | null;
}

/** 🔒 A file row's restriction pill (piece 9): its words and its status group. */
export interface DocumentFileFlag {
  text: string;
  group: 'warning' | 'inactive';
}

/** 🔒 Whether a file row answers a filter: its name or its served version's stored name holds the typed text, case aside (T1741; decision 2713). */
export function documentFileMatches(row: Pick<DocumentFileRow, 'filename' | 'storedName'>, typed: string): boolean {
  const wanted = typed.trim().toLowerCase();
  if (wanted === '') return true;
  return row.filename.toLowerCase().includes(wanted) || (row.storedName ?? '').toLowerCase().includes(wanted);
}

/** One document an order raised, as its title bar links to it (decision 0863). */
export interface SalesOrderRaised {
  id: string;
  number: string;
  /** 🔒 The raised document's own status, which its link draws as a pill beside the number (decision 10745; T982); absent where a caller names none. */
  status?: string | null;
}

/**
 * 🔒 The order's two raises (decision 0863, the owner's T282): why the order
 * raises nothing yet, where it does not, and the document each raise opened,
 * which the title bar reads as *Opened* with a link.
 */
export interface SalesOrderRaiseView {
  /** Why this order raises nothing at its status, or null where it may. */
  refusal: string | null;
  purchaseOrder: SalesOrderRaised | null;
  workOrder: SalesOrderRaised | null;
  /** How many of the order's lines are Service lines, which is what a Work Order carries. */
  serviceLineCount: number;
  /** Whether the order has a ship-to a drop-shipped purchase order can go to. */
  canDropShip: boolean;
}

/** One line of the order short of stock at a site, as the raise window lists it (decision 0863). */
export interface SalesOrderShortLine {
  /** The order line's id, or on a Project order the BOM row's id where the line rolled up BOM rows; the window uses it only as a key (T1815). */
  lineId: string;
  lineNumber: number;
  sku: string | null;
  description: string;
  /** What goes on the purchase order: the outstanding quantity less what the site holds unclaimed. */
  shortQuantity: number;
  /** What the site covers from stock, drawn beside it so a partly covered line reads as one. */
  fromStock: number;
  unitName: string;
}

/** 🔒 What Raise a purchase order sends (decision 0863): the vendor, our site, and the drop-ship choice, off by default. */
export interface RaisePurchaseOrderRequest {
  vendorCompanyId: string;
  siteId: string;
  dropShip?: boolean;
}

/**
 * 🔨 Why an order raises nothing at its status (decision 0863): either raise
 * stands on a Released or a Closed order, the release question a Work Order and
 * a Shipment already ask, so the three agree.
 */
export function salesOrderRaiseProblem(status: SalesOrderStatusValue): string | null {
  return releasedForWorkProblem(status);
}
